import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import CyberLayout from '@/components/CyberLayout';
import { toast } from 'sonner';
import axios from 'axios';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileText, ArrowRight, ArrowLeft, Loader2, Edit, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JSON_LABELS = {
  nome: 'Nome', indirizzo: 'Indirizzo', citta: 'Città', lavoro: 'Lavoro', veicolo: 'Veicolo',
  targa: 'Targa', marca: 'Marca', modello: 'Modello', colore: 'Colore',
  conosciuti: 'Come si sono conosciuti', descrizione: 'Descrizione fisica', altro: 'Altro',
  facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok', linkedin: 'LinkedIn', altri: 'Altri'
};

const formatAnswer = (answer) => {
  if (typeof answer !== 'string' || !answer.startsWith('{')) return answer;
  try {
    return Object.entries(JSON.parse(answer))
      .filter(([, v]) => v && String(v).trim())
      .map(([k, v]) => `${JSON_LABELS[k] || k}: ${v}`)
      .join('\n');
  } catch {
    return answer;
  }
};

export default function Summary() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [selectedType, setSelectedType] = useState(null);
  const [answers, setAnswers] = useState({});
  const [photos, setPhotos] = useState([]);
  const [loverPhotos, setLoverPhotos] = useState([]);
  const [evidencePhotos, setEvidencePhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const privacyAccepted = sessionStorage.getItem('privacyAccepted');
    const typeData = sessionStorage.getItem('selectedType');
    const answersData = sessionStorage.getItem('answers');
    const photosData = sessionStorage.getItem('photos');
    
    if (!privacyAccepted || !typeData || !answersData) {
      navigate('/select-type');
      return;
    }
    
    setSelectedType(JSON.parse(typeData));
    setAnswers(JSON.parse(answersData));
    if (photosData) {
      setPhotos(JSON.parse(photosData));
    }
    setLoverPhotos(JSON.parse(sessionStorage.getItem('loverPhotos') || '[]'));
    setEvidencePhotos(JSON.parse(sessionStorage.getItem('evidencePhotos') || '[]'));
  }, [navigate]);

  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      // Get access code and email from session
      const accessCode = sessionStorage.getItem('accessCode');
      const clientEmail = sessionStorage.getItem('clientEmail');
      
      const response = await axios.post(`${API}/submissions`, {
        investigation_type_id: selectedType.id,
        language: language,
        answers: answers,
        photos: photos,
        lover_photos: loverPhotos,
        evidence_photos: evidencePhotos,
        privacy_accepted: true,
        privacy_accepted_at: sessionStorage.getItem('privacyAcceptedAt'),
        access_code: accessCode || null,
        client_email: clientEmail || null
      });
      
      sessionStorage.setItem('submissionId', response.data.id);
      
      // Clear session data
      sessionStorage.removeItem('selectedType');
      sessionStorage.removeItem('answers');
      sessionStorage.removeItem('photos');
      sessionStorage.removeItem('loverPhotos');
      sessionStorage.removeItem('evidencePhotos');
      sessionStorage.removeItem('accessCode');
      sessionStorage.removeItem('accessCodeType');
      sessionStorage.removeItem('clientEmail');
      sessionStorage.removeItem('clientName');
      
      navigate('/thank-you');
    } catch (error) {
      console.error(error);
      toast.error(t('error'));
    } finally {
      setSubmitting(false);
    }
  };

  const answerEntries = Object.entries(answers);

  return (
    <CyberLayout>
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-20">
        <div className="max-w-3xl w-full">
          {/* Header */}
          <div className="text-center mb-10 fade-in-up">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cyber-gray border border-neon-green/30 mb-6 neon-glow">
              <FileText className="w-8 h-8 text-neon-green" />
            </div>
            <h1 className="font-orbitron text-3xl font-bold text-white mb-2 tracking-wider">
              {t('summaryTitle')}
            </h1>
            <p className="font-mono text-neon-cyan text-sm tracking-widest uppercase">
              {t('summarySubtitle')}
            </p>
          </div>

          {/* Investigation Type */}
          <div className="cyber-card rounded-lg p-6 mb-6 fade-in-up" style={{ animationDelay: '0.1s' }}>
            <div className="font-mono text-xs text-neon-green/70 mb-2 uppercase tracking-wider">
              {t('type')}
            </div>
            <div className="font-orbitron text-xl text-white">
              {selectedType?.[`name_${language}`] || selectedType?.name_it}
            </div>
          </div>

          {/* Answers */}
          <div className="cyber-card rounded-lg p-6 mb-6 fade-in-up" style={{ animationDelay: '0.2s' }}>
            <div className="font-mono text-xs text-neon-green/70 mb-4 uppercase tracking-wider">
              {t('answers')} ({answerEntries.length})
            </div>
            
            <ScrollArea className="h-[300px] pr-4">
              <div className="space-y-4">
                {answerEntries.map(([question, answer], index) => (
                  <div 
                    key={index} 
                    className="border-b border-white/10 pb-4 last:border-0"
                  >
                    <div className="font-mono text-xs text-neon-cyan mb-1">
                      Q{String(index + 1).padStart(2, '0')}
                    </div>
                    <div className="text-gray-300 text-sm mb-2">
                      {question}
                    </div>
                    <div className="font-mono text-white bg-cyber-surface/50 px-3 py-2 rounded whitespace-pre-line" data-testid={`summary-answer-${index}`}>
                      {formatAnswer(answer) || '-'}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Photos */}
          {(photos.length > 0 || loverPhotos.length > 0 || evidencePhotos.length > 0) && (
            <div className="cyber-card rounded-lg p-6 mb-8 fade-in-up" style={{ animationDelay: '0.3s' }}>
              <div className="font-mono text-xs text-neon-green/70 mb-4 uppercase tracking-wider">
                {t('photos')} ({photos.length})
              </div>
              <div className="flex gap-2 flex-wrap">
                {photos.map((_, index) => (
                  <div 
                    key={index}
                    className="w-16 h-16 bg-cyber-surface rounded border border-neon-green/30 flex items-center justify-center"
                  >
                    <Check className="w-6 h-6 text-neon-green" />
                  </div>
                ))}
              </div>
              {loverPhotos.length > 0 && (
                <div className="font-mono text-xs text-pink-400/80 mt-4 uppercase tracking-wider" data-testid="summary-lover-photos-count">
                  {language === 'it' ? 'Foto presunto amante' : 'Suspected lover photos'}: {loverPhotos.length}
                </div>
              )}
              {evidencePhotos.length > 0 && (
                <div className="font-mono text-xs text-yellow-400/80 mt-2 uppercase tracking-wider" data-testid="summary-evidence-photos-count">
                  {language === 'it' ? 'Prove raccolte' : 'Collected evidence'}: {evidencePhotos.length}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 fade-in-up" style={{ animationDelay: '0.4s' }}>
            <button
              onClick={() => navigate('/questionnaire')}
              className="cyber-btn-outline px-6 py-3 flex items-center justify-center gap-2 rounded-md flex-1"
            >
              <Edit className="w-4 h-4" />
              <span>{t('editAnswers')}</span>
            </button>
            
            <button
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="submit-report-btn"
              className="cyber-btn px-6 py-3 flex items-center justify-center gap-2 flex-1"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <span>{t('submitReport')}</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </CyberLayout>
  );
}
